import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import './InputFile.scss';
import { Dropzone } from '@mantine/dropzone';
// Стили зоны — вместе с компонентом: ControlPanel, в отличие от Front, их глобально не подключает.
import '@mantine/dropzone/styles.layer.css';
import type { IFileData } from '../../types/IFileData';
import type { IButtonProps } from '../Button';
import { getBase64, validateFile } from './fileUtils';
import Attachment from '../../molecules/Attachment';
import clsx from 'clsx';
import { MdCloudUpload, MdInsertDriveFile, MdScale, MdLayers } from 'react-icons/md';
import AcceptedFileFormats from './AcceptedFileFormats/AcceptedFileFormats';
import './InputFileCompact.scss';
import { formatBytes } from '../../lib/formatBytes';
import { useUi } from '../../provider/UiContext';

/**
 * Файловый инпут для небольших файлов, по умолчанию конвертирует файл в base64.
 * Передает в `onChange` выбранные файлы с их base64-версией { file: File, base64: string }
 *
 */
export interface IFileInputProps extends Omit<IButtonProps, 'onError' | 'onChange'> {
    /** Компактная зона с раскрываемым перечнем форматов. */
    compact?: boolean;
    /** Подпись ограничения размера (по умолчанию — на файл). */
    sizeLimitLabel?: string;
    /** Имя инпута */
    name?: string;
    /** Разрешенные типы файлов */
    accept?: string;
    /** Мультивыбор файлов */
    multiple?: boolean;
    /** className */
    className?: string;
    /** Недоступный */
    disabled?: boolean;
    /** Плейсхолдер кнопки */
    placeholderButton?: string;
    /** Плейсхолдер */
    placeholderDropzone?: string;
    /** Начальные файлы */
    files?: IFileData[];
    /** Выбранные файлы — весь список после добавления или удаления */
    onChange: (files: IFileData[]) => Promise<void> | void;
    /** Очищать файлы после выстреливания события set (нужно там где программно обруливаем результат выборки, чтобы повторно не залетали) */
    clearFilesAfterSet?: boolean;
    /** Колбек при ошибке */
    onError?: (err: Error) => void;
    /** Максимальный размер - kB */
    maxSize?: number;
    /** Количество файлов */
    count?: number;
    /** Показывать чипы файлов */
    showChips?: boolean;
    /** Способ скачивания файлов */
    customDownloadMethod?: boolean;
    /** Условие для удаления вложений */
    showRemoveIcon?: boolean;
    /**
     * Что отдавать в `onChange`: `base64` — файл и его data-URL; `file` — только файл (`base64` пустой), без чтения
     * содержимого — когда файл уходит на сервер как есть (окна импорта).
     * @default 'base64'
     */
    readAs?: 'base64' | 'file';
    /**
     * Сообщать о недопустимом файле уведомлением провайдера (`notify`). `false` — только `onError`: окно, которое
     * показывает ошибку у самого поля, не дублирует её тостом.
     * @default true
     */
    notifyErrors?: boolean;
    /** Невалидное поле — рамка зоны цветом ошибки (сообщение показывает владелец поля) */
    invalid?: boolean;
}

const pluralizeFiles = (n: number): string => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'файл';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'файла';
    return 'файлов';
};

const formatAccept = (accept?: string): string | null => {
    if (!accept || accept === '*' || accept === '*/*') return null;
    if (accept === 'image/*') return 'Изображения';
    if (accept === 'video/*') return 'Видео';
    if (accept === 'audio/*') return 'Аудио';

    return (
        accept
            .split(',')
            .map((s) => s.trim().replace(/^\./, '').toUpperCase())
            .filter(Boolean)
            .join(' · ') || null
    );
};

// Фасад: зона — Mantine Dropzone (react-dropzone под капотом): корректные
// drag-события (самодельный dragleave мигал на проходе над дочерними узлами), клик/клавиатура
// для диалога, скрытый input — внутри. Валидация НЕ делегирована Dropzone: validateFile
// оставляет русские ошибки и семантику «принять валидные, отклонить лишь битые», поэтому
// зона без ограничений, а onDropAny собирает и accepted, и rejected (reject возможен только
// от multiple=false при дропе пачки — легаси такую пачку обрабатывал). Подсветка drag —
// [data-accept] вместо класса --active, текст — слоты Dropzone.Accept/Idle.
const InputFile: React.FC<IFileInputProps> = ({
    compact = false,
    sizeLimitLabel,
    name = '',
    accept = '*',
    multiple = true,
    className = '',
    disabled = false,
    placeholderButton = 'Выберите файлы с устройства',
    placeholderDropzone = 'Перетащите файлы в эту область',
    files = [],
    onChange,
    onError,
    maxSize,
    count,
    clearFilesAfterSet = false,
    showChips = true,
    showRemoveIcon = true,
    readAs = 'base64',
    notifyErrors = true,
    invalid = false,
}: IFileInputProps) => {
    const { notify } = useUi();
    const [file, uploadFile] = useState<IFileData[]>(() => files);
    const metaId = useId();
    const openRef = useRef<() => void>(null);

    const acceptLabel = useMemo(() => formatAccept(accept), [accept]);
    const hasMeta = !!(acceptLabel || maxSize || count);

    const totalSize = useMemo(() => file.reduce((sum, f) => sum + (f.file?.size ?? 0), 0), [file]);

    const handleFiles = useCallback(
        async (dropped: File[]) => {
            const promises: Promise<IFileData>[] = [];

            dropped.slice(0, count).forEach((fl: File) => {
                const validationResult = validateFile(fl, {
                    maxSize,
                    accept,
                    onInvalid: notifyErrors
                        ? (message) => notify({ title: 'Ошибка загрузки файла', message, variant: 'red' })
                        : undefined,
                });

                if (validationResult.valid) {
                    promises.push(readAs === 'file' ? Promise.resolve({ file: fl, base64: '' }) : getBase64(fl));
                } else {
                    onError && onError(new Error(validationResult.error));
                }
            });

            try {
                const data = await Promise.all(promises);

                let newFiles: IFileData[] = [];

                if (multiple) {
                    const keysMap: Record<string, boolean> = {};
                    const next = [...file, ...data].slice(0, count);

                    next.forEach((f: IFileData) => {
                        if (!keysMap[f.file.name + f.file.lastModified]) {
                            keysMap[f.file.name + f.file.lastModified] = true;
                            newFiles.push(f);
                        }
                    });
                } else {
                    newFiles.push(...data);
                }

                await onChange(newFiles);

                if (clearFilesAfterSet) {
                    newFiles = [];
                }

                uploadFile(newFiles);
            } catch (error) {
                console.log('%c [Ошибка] Не удалось загрузить файл(ы)', 'color: #FF5722');
                console.log(error);

                onError && onError(new Error(`Не удалось загрузить ${multiple ? 'файлы' : 'файл'}`));
            }
        },
        [accept, clearFilesAfterSet, count, file, maxSize, multiple, notify, notifyErrors, onError, readAs, onChange],
    );

    const onRemove = useCallback(
        (index: number) => {
            const newListFile = file;
            newListFile.splice(index, 1);

            if (!newListFile.length) {
                uploadFile([]);
                onChange([]);
            } else {
                uploadFile([...newListFile]);
                onChange([...newListFile]);
            }
        },
        [file, onChange],
    );

    const onClearAll = useCallback(() => {
        uploadFile([]);
        onChange([]);
    }, [onChange]);

    return (
        <div
            className={clsx(
                'rf-file-input__wrapper',
                compact && 'rf-file-input--compact',
                invalid && 'rf-file-input--invalid',
                className,
            )}
        >
            <Dropzone
                openRef={openRef}
                activateOnClick
                activateOnKeyboard={!compact}
                tabIndex={compact ? -1 : 0}
                onDrop={() => {}}
                // валидация своя (validateFile) — Dropzone без ограничений; reject возможен
                // только при multiple=false и дропе нескольких файлов, легаси обрабатывал всё
                onDropAny={(accepted, rejections) => {
                    void handleFiles([...accepted, ...rejections.map((r) => r.file)]);
                }}
                multiple={multiple}
                disabled={disabled}
                name={name}
                classNames={{
                    root: clsx('rf-file-input__dropzone', disabled && 'rf-file-input__dropzone--disabled'),
                    inner: 'rf-file-input__inner',
                }}
                inputProps={{
                    className: 'rf-input__file-hidden',
                    // фильтр нативного диалога, как у легаси-input (валидация всё равно своя)
                    accept: accept === '*' ? undefined : accept,
                    'aria-describedby': hasMeta ? metaId : undefined,
                    'aria-label': compact ? 'Выбрать файлы' : placeholderButton,
                    // повторный выбор того же файла должен стрелять onChange (легаси
                    // сбрасывал value после обработки)
                    onClick: (e) => {
                        e.currentTarget.value = '';
                    },
                }}
            >
                {compact ? (
                    <>
                        <span className="rf-file-input__compact-icon" aria-hidden>
                            <MdCloudUpload />
                        </span>
                        <div className="rf-file-input__compact-body">
                            <div className="rf-file-input__compact-actions">
                                <div className="rf-file-input__compact-title">
                                    <Dropzone.Idle>{placeholderDropzone}</Dropzone.Idle>
                                    <Dropzone.Accept>Отпустите, чтобы добавить</Dropzone.Accept>
                                    <Dropzone.Reject>Отпустите, чтобы добавить</Dropzone.Reject>
                                </div>
                                <div className="rf-file-input__compact-hint">
                                    <span>или</span>
                                    <button
                                        type="button"
                                        className="rf-file-input__compact-browse"
                                        disabled={disabled}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            openRef.current?.();
                                        }}
                                    >
                                        {placeholderButton}
                                    </button>
                                </div>
                            </div>
                            <div id={metaId}>
                                <AcceptedFileFormats accept={accept} />
                            </div>
                        </div>
                        {(maxSize !== undefined || count !== undefined) && (
                            <div className="rf-file-input__compact-limit">
                                {maxSize !== undefined && (
                                    <span>
                                        До {formatBytes(maxSize * 1024)}
                                        <small>{sizeLimitLabel ?? 'на файл'}</small>
                                    </span>
                                )}
                                {count !== undefined && (
                                    <span>
                                        До {count} {pluralizeFiles(count)}
                                    </span>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <span className="rf-file-input__icon-wrap" aria-hidden="true">
                            <span className="rf-file-input__icon-halo" />
                            <MdCloudUpload className="rf-file-input__icon" />
                        </span>

                        <div className="rf-file-input__body">
                            <div className="rf-file-input__title">
                                <Dropzone.Accept>Отпустите, чтобы загрузить</Dropzone.Accept>
                                {/* reject = multiple:false + пачка файлов; onDropAny всё равно обработает
                            дроп (легаси принимал пачку), поэтому текст тот же — иначе заголовок
                            пустел на время drag-over */}
                                <Dropzone.Reject>Отпустите, чтобы загрузить</Dropzone.Reject>
                                <Dropzone.Idle>{placeholderDropzone}</Dropzone.Idle>
                            </div>
                            <div className="rf-file-input__hint">
                                <span className="rf-file-input__hint-divider">или</span>
                                <span className="rf-file-input__browse">{placeholderButton}</span>
                            </div>

                            {hasMeta && (
                                <div className="rf-file-input__meta" id={metaId}>
                                    {acceptLabel && (
                                        <span className="rf-file-input__meta-chip">
                                            <MdInsertDriveFile aria-hidden />
                                            <span>{acceptLabel}</span>
                                        </span>
                                    )}
                                    {maxSize !== undefined && (
                                        <span className="rf-file-input__meta-chip">
                                            <MdScale aria-hidden />
                                            <span>до {formatBytes(maxSize * 1024)}</span>
                                        </span>
                                    )}
                                    {count !== undefined && (
                                        <span className="rf-file-input__meta-chip">
                                            <MdLayers aria-hidden />
                                            <span>
                                                до {count} {pluralizeFiles(count)}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Dropzone>

            {showChips && file.length > 0 && (
                <div className="rf-file-input__files">
                    <div className="rf-file-input__files-header">
                        <span className="rf-file-input__files-count">
                            <strong>{file.length}</strong> {pluralizeFiles(file.length)}
                            {totalSize > 0 && (
                                <>
                                    <span className="rf-file-input__files-dot" aria-hidden>
                                        •
                                    </span>
                                    {formatBytes(totalSize)}
                                </>
                            )}
                        </span>
                        {file.length > 1 && showRemoveIcon && !disabled && (
                            <button type="button" className="rf-file-input__clear-all" onClick={onClearAll}>
                                Очистить все
                            </button>
                        )}
                    </div>
                    <div className="rf-file-input__attachments" aria-live="polite">
                        {file.map((attachment: IFileData, index: number) => (
                            <Attachment
                                key={`${attachment.file.name}-${attachment.file.lastModified}-${index}`}
                                attachment={attachment}
                                showRemoveIcon={showRemoveIcon}
                                onRemove={() => onRemove(index)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InputFile;
